import type { InjectionKey, Ref } from 'vue'

export const GTM_LIST_NAME = Symbol('gtm-list-name') as InjectionKey<string>

export const useSearchListingViewed = (target: Ref<any>, cars: Readonly<Ref<IProductModel[] | null | undefined>>, listName: string) => {
	provide(GTM_LIST_NAME, listName)

	if (process.server) return { send: () => {} }

	const isVisible = ref(false)
	const lastSentKey = ref<string | null>(null)
	const bookingStore = useBookingStore()

	const buildParams = (list: IProductModel[]) => {
		const days = bookingStore.getDays ?? 1
		const prices = list.map(
			(car) => car.segment.promotionalPrice?.amountGross ?? car.segment.regularPrice?.amountGross ?? car.segment.pricePerDay,
		)
		const uniqueSegments = new Map()
		list.forEach((car) => {
			const { id, nameEN } = car.segment
			if (!uniqueSegments.has(id)) {
				uniqueSegments.set(id, nameEN)
			}
		})

		return {
			item_list_name: listName,
			price_minimum: prices.length ? Number((Math.min(...prices) * days).toFixed(2)) : null,
			price_maximum: prices.length ? Number((Math.max(...prices) * days).toFixed(2)) : null,
			available_car_id: list.map((car) => car.id),
			available_car_name: list.map((car) => getCarNameLabel(car)),
			available_segments_id: Array.from(uniqueSegments.keys()),
			available_segments_name: Array.from(uniqueSegments.values()),
			available_cars: list.map((car) => ({
				car_id: car.id ?? null,
				car_name: getCarNameLabel(car),
				car_brand: car.brand || null,
				segments_id: car.segment?.id ?? null,
				segments_name: getCarSegmentLabel(car) ?? null,
			})),
		}
	}

	const sendEvent = () => {
		const list = cars.value ?? []
		if (!list.length) return

		const key = list.map((car) => car.id).join(',')
		if (key === lastSentKey.value) return

		const gtmService = useGTMService()
		if (!gtmService) return

		lastSentKey.value = key
		gtmService.searchListingViewed(buildParams(list))
	}

	useIntersectionObserver(
		target,
		([entry]) => {
			isVisible.value = !!entry?.isIntersecting
			if (isVisible.value) sendEvent()
		},
		{ threshold: 0.1 },
	)

	watch(cars, () => {
		if (isVisible.value) sendEvent()
	})

	return { send: sendEvent }
}
